import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Stepper, Step, StepLabel,
  Card, CardContent, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Chip, Slider,
  Alert, CircularProgress, ToggleButtonGroup, ToggleButton,
  Skeleton, Tooltip, IconButton,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Speed, Bolt, CurrencyBitcoin, Info, Shield } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { paperTradingApi, BudgetOverview } from '../../services/paperTradingApi';
import { apiFetch } from '../../utils/apiFetch';



const STRATEGY_TYPES = [
  { value: 'marktmeister', label: 'MarktMeister', desc: 'Adaptiver Bot — erkennt Marktphasen und passt Strategie automatisch an', icon: '🧙', experience: 'Erfahren' },
];

const STRATEGY_LABELS: Record<string, string> = {
  marktmeister: 'MarktMeister',
};

const INTERVAL_LABELS: Record<number, string> = {
  60: '1 Minute', 300: '5 Minuten', 900: '15 Minuten', 3600: '1 Stunde', 86400: '1 Tag',
};

// Erlaubte Chart-Zeiträume pro Strategie (muss mit Backend allowed_intervals übereinstimmen)
const ALLOWED_CHART_INTERVALS: Record<string, { value: string; label: string }[]> = {
  marktmeister:          [{ value: '5m', label: '5 Minuten' }, { value: '15m', label: '15 Minuten' }, { value: '1h', label: '1 Stunde' }],
};

// Standard-Chart-Zeitraum pro Strategie
const DEFAULT_CHART_INTERVAL: Record<string, string> = {
  marktmeister: '5m',
};

// Standard Stop-Loss / Take-Profit pro Strategietyp
const RISK_DEFAULTS: Record<string, { sl: number; tp: number; slReason: string; tpReason: string }> = {
  marktmeister: {
    sl: 3, tp: 6,
    slReason: 'Der MarktMeister nutzt einen ATR-basierten Trailing Stop — 3% als Sicherheitsnetz schützt auch wenn der Bot sich verläuft.',
    tpReason: 'Der Bot nimmt Gewinne automatisch über den Signal-Aggregator mit. 6% als festes Ziel dient als zusätzliche Absicherung.',
  },
};

const SL_TOOLTIP = 'Stop-Loss: Automatischer Verkauf wenn der Kurs um diesen Prozentsatz fällt. Schützt dein Kapital vor großen Verlusten. Ein engerer Stop (1-2%) bedeutet weniger Risiko pro Trade, aber häufigeres Auslösen. Ein weiter Stop (4-6%) gibt dem Trade mehr Raum, riskiert aber mehr pro Trade.';

const TP_TOOLTIP = 'Take-Profit: Automatischer Verkauf wenn der Kurs um diesen Prozentsatz steigt. Sichert Gewinne bevor der Kurs wieder fällt. Ein niedriges Ziel (1-3%) wird häufiger erreicht, ein hohes Ziel (5-15%) seltener, bringt aber mehr pro Trade.';

interface Recommendation {
  market: string;
  coin: string;
  price: number;
  change_24h_pct: number;
  volatility_pct: number;
  recommended_strategy: string;
  strategy_reason: string;
  recommended_interval: number;
  interval_reason: string;
  signal: string;
}

const StrategyWizard: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [budgetData, setBudgetData] = useState<BudgetOverview | null>(null);

  // Mode: 'strategy' = pick strategy first, 'crypto' = pick crypto first (get recommendation)
  const [mode, setMode] = useState<'strategy' | 'crypto'>('strategy');

  // Recommendations (for crypto-first mode)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [selectedRec, setSelectedRec] = useState<Recommendation | null>(null);

  // Form state
  const [strategyType, setStrategyType] = useState('marktmeister');
  const [name, setName] = useState('');
  const [market, setMarket] = useState('BTC-EUR');
  const [budgetEur, setBudgetEur] = useState(1000);
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [stopLoss, setStopLoss] = useState<number>(3);
  const [takeProfit, setTakeProfit] = useState<number>(6);
  const [chartInterval, setChartInterval] = useState<string>(DEFAULT_CHART_INTERVAL['marktmeister']);
  // Mindest-Trade-Betrag in EUR. Verhindert dass die 0,25% Bitvavo-Fee
  // die Rendite kleiner Trades aufzehrt. 0 = kein Limit (Bitvavo-Default 5).
  const [minTradeAmount, setMinTradeAmount] = useState<number>(0);
  const [minTradeBehavior, setMinTradeBehavior] = useState<'skip' | 'round_up'>('skip');

  useEffect(() => {
    paperTradingApi.getBudget().then(setBudgetData).catch(console.error);
  }, []);

  // SL/TP-Defaults und Chart-Interval laden wenn Strategietyp wechselt
  useEffect(() => {
    const defaults = RISK_DEFAULTS[strategyType];
    if (defaults) {
      setStopLoss(defaults.sl);
      setTakeProfit(defaults.tp);
    }
    setChartInterval(DEFAULT_CHART_INTERVAL[strategyType] || '1h');
  }, [strategyType]);

  const fetchRecommendations = () => {
    setLoadingRecs(true);
    apiFetch('/paper/recommendations', {
      headers: {},
    })
      .then((r) => r.json())
      .then((data) => setRecommendations(data.recommendations || []))
      .catch(console.error)
      .finally(() => setLoadingRecs(false));
  };

  const handleSelectCrypto = (rec: Recommendation) => {
    setSelectedRec(rec);
    setMarket(rec.market);
    setStrategyType(rec.recommended_strategy);
    setIntervalSeconds(rec.recommended_interval);
    setName(`${STRATEGY_LABELS[rec.recommended_strategy] || rec.recommended_strategy} — ${rec.coin}`);
  };

  const handleModeChange = (_: any, newMode: 'strategy' | 'crypto' | null) => {
    if (!newMode) return;
    setMode(newMode);
    if (newMode === 'crypto' && recommendations.length === 0) {
      fetchRecommendations();
    }
  };

  const availableBudget = budgetData?.available_eur ?? 0;

  const steps = mode === 'crypto'
    ? ['Krypto wählen', 'Empfehlung prüfen', 'Risikomanagement', 'Budget zuweisen']
    : ['Strategie wählen', 'Konfigurieren', 'Risikomanagement', 'Budget zuweisen'];

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/paper/strategies/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name || `${STRATEGY_LABELS[strategyType] || strategyType} Strategie`,
          strategy_type: strategyType,
          market,
          config: { interval: chartInterval, market },
          interval_seconds: intervalSeconds,
          stop_loss_percentage: stopLoss,
          take_profit_percentage: takeProfit,
          ...(minTradeAmount > 0 ? {
            min_trade_amount_eur: minTradeAmount,
            min_trade_behavior: minTradeBehavior,
          } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Fehler beim Erstellen');
      }
      const strategy = await res.json();

      await apiFetch(`/paper/strategies/${strategy.id}/budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget_eur: budgetEur }),
      });

      navigate('/strategies');
    } catch (e: any) {
      setError(e.message || 'Fehler beim Erstellen der Strategie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Neue Strategie erstellen</Typography>

      {/* Mode toggle */}
      {activeStep === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <ToggleButtonGroup exclusive value={mode} onChange={handleModeChange} size="small">
            <ToggleButton value="strategy" sx={{ px: 3, textTransform: 'none' }}>
              <Bolt sx={{ mr: 1, fontSize: 18 }} /> Strategie wählen
            </ToggleButton>
            <ToggleButton value="crypto" sx={{ px: 3, textTransform: 'none' }}>
              <CurrencyBitcoin sx={{ mr: 1, fontSize: 18 }} /> Krypto wählen (mit Empfehlung)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* ============ STRATEGY-FIRST MODE ============ */}
      {mode === 'strategy' && activeStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Welche Strategie möchtest du nutzen?</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {STRATEGY_TYPES.map((s) => (
              <Card
                key={s.value}
                onClick={() => setStrategyType(s.value)}
                sx={{
                  cursor: 'pointer', borderRadius: 2,
                  border: 2, borderColor: strategyType === s.value ? 'primary.main' : 'divider',
                  bgcolor: strategyType === s.value ? 'action.selected' : 'background.paper',
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5">{s.icon}</Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={600}>{s.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
                    </Box>
                    <Chip
                      label={s.experience}
                      size="small"
                      color={s.experience === 'Anfänger' ? 'success' : s.experience === 'Mittel' ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={() => setActiveStep(1)}>Weiter</Button>
          </Box>
        </Box>
      )}

      {mode === 'strategy' && activeStep === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Strategie konfigurieren</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth label="Name der Strategie" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${strategyType.toUpperCase()} Strategie`}
            />
            <FormControl fullWidth>
              <InputLabel>Markt</InputLabel>
              <Select value={market} label="Markt" onChange={(e) => setMarket(e.target.value)}>
                {[
                  { value: 'BTC-EUR', label: 'Bitcoin (BTC)' },
                  { value: 'ETH-EUR', label: 'Ethereum (ETH)' },
                  { value: 'XRP-EUR', label: 'Ripple (XRP)' },
                  { value: 'SOL-EUR', label: 'Solana (SOL)' },
                  { value: 'ADA-EUR', label: 'Cardano (ADA)' },
                ].map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Chart-Zeitraum (Kerzengröße)</InputLabel>
              <Select value={chartInterval} label="Chart-Zeitraum (Kerzengröße)" onChange={(e) => setChartInterval(e.target.value)}>
                {(ALLOWED_CHART_INTERVALS[strategyType] || []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: -1 }}>
              Der Chart-Zeitraum bestimmt, wie groß die Kerzen sind, die die Strategie analysiert.
              Kürzere Zeiträume (1m, 5m) = mehr Signale, schnellere Trades.
              Längere Zeiträume (4h, 1d) = weniger Signale, größere Moves.
            </Alert>
            <FormControl fullWidth>
              <InputLabel>Prüf-Intervall</InputLabel>
              <Select value={intervalSeconds} label="Prüf-Intervall" onChange={(e) => setIntervalSeconds(Number(e.target.value))}>
                <MenuItem value={60}>1 Minute</MenuItem>
                <MenuItem value={300}>5 Minuten</MenuItem>
                <MenuItem value={900}>15 Minuten</MenuItem>
                <MenuItem value={3600}>1 Stunde</MenuItem>
                <MenuItem value={86400}>1 Tag</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setActiveStep(0)}>Zurück</Button>
            <Button variant="contained" onClick={() => setActiveStep(2)}>Weiter zum Risikomanagement</Button>
          </Box>
        </Box>
      )}

      {/* ============ CRYPTO-FIRST MODE ============ */}
      {mode === 'crypto' && activeStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Wähle einen Krypto — wir empfehlen die beste Strategie</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Basierend auf aktueller Volatilität, Preisentwicklung und Handelsvolumen
          </Typography>

          {loadingRecs ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {recommendations.map((rec) => {
                const isSelected = selectedRec?.market === rec.market;
                const signalColor = rec.signal === 'bullish' ? 'success' : rec.signal === 'bearish' ? 'error' : 'default';
                const changeColor = rec.change_24h_pct >= 0 ? 'success.main' : 'error.main';

                return (
                  <Card
                    key={rec.market}
                    onClick={() => handleSelectCrypto(rec)}
                    sx={{
                      cursor: 'pointer', borderRadius: 2,
                      border: 2, borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h5" fontWeight={700}>{rec.coin}</Typography>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              €{rec.price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" sx={{ color: changeColor }}>
                              {rec.change_24h_pct >= 0 ? '+' : ''}{rec.change_24h_pct}% (24h)
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip
                            icon={rec.signal === 'bullish' ? <TrendingUp /> : rec.signal === 'bearish' ? <TrendingDown /> : <TrendingFlat />}
                            label={rec.signal}
                            size="small"
                            color={signalColor as any}
                            variant="outlined"
                          />
                          <Chip label={`Vol: ${rec.volatility_pct}%`} size="small" variant="outlined" />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!selectedRec}>
              Weiter
            </Button>
          </Box>
        </Box>
      )}

      {mode === 'crypto' && activeStep === 1 && selectedRec && (
        <Box>
          <Typography variant="h6" gutterBottom>Unsere Empfehlung für {selectedRec.coin}</Typography>

          {/* Recommendation Card */}
          <Card sx={{ borderRadius: 3, border: 2, borderColor: 'primary.main', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{selectedRec.coin}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    €{selectedRec.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })} ·{' '}
                    <span style={{ color: selectedRec.change_24h_pct >= 0 ? '#4CAF50' : '#EF5350' }}>
                      {selectedRec.change_24h_pct >= 0 ? '+' : ''}{selectedRec.change_24h_pct}%
                    </span>
                    {' '}· Volatilität: {selectedRec.volatility_pct}%
                  </Typography>
                </Box>
              </Box>

              {/* Strategy Recommendation */}
              <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, backgroundColor: 'rgba(76, 175, 80, 0.08)', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="success.main" gutterBottom>
                  Empfohlen: {STRATEGY_LABELS[selectedRec.recommended_strategy] || selectedRec.recommended_strategy}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {selectedRec.strategy_reason}
                </Typography>
              </Box>

              {/* Interval Recommendation */}
              <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.08)', borderRadius: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Speed color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Empfohlenes Intervall: {INTERVAL_LABELS[selectedRec.recommended_interval] || `${selectedRec.recommended_interval}s`}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedRec.interval_reason}
                </Typography>
              </Box>

              {/* Allow override */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Du kannst anpassen:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth label="Name" value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Strategie</InputLabel>
                    <Select value={strategyType} label="Strategie" onChange={(e) => setStrategyType(e.target.value)}>
                      {STRATEGY_TYPES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.icon} {s.label}
                          {s.value === selectedRec.recommended_strategy && ' ✓'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Intervall</InputLabel>
                    <Select value={intervalSeconds} label="Intervall" onChange={(e) => setIntervalSeconds(Number(e.target.value))}>
                      {Object.entries(INTERVAL_LABELS).map(([val, label]) => (
                        <MenuItem key={val} value={Number(val)}>
                          {label}
                          {Number(val) === selectedRec.recommended_interval && ' ✓'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setActiveStep(0)}>Zurück</Button>
            <Button variant="contained" onClick={() => setActiveStep(2)}>Weiter zum Risikomanagement</Button>
          </Box>
        </Box>
      )}

      {/* ============ RISIKOMANAGEMENT STEP (shared) ============ */}
      {activeStep === 2 && (() => {
        const defaults = RISK_DEFAULTS[strategyType];
        const slIsDefault = defaults && stopLoss === defaults.sl;
        const tpIsDefault = defaults && takeProfit === defaults.tp;
        return (
        <Box>
          <Typography variant="h6" gutterBottom>
            <Shield sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
            Risikomanagement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Stop-Loss und Take-Profit schützen dein Kapital automatisch. Die Werte sind für die gewählte Strategie voreingestellt.
          </Typography>

          {/* Stop-Loss */}
          <Card sx={{ borderRadius: 2, mb: 2, border: 1, borderColor: 'error.main', borderStyle: 'solid' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="error.main">
                  Stop-Loss: {stopLoss}%
                </Typography>
                {slIsDefault && <Chip label="Empfohlen" size="small" color="success" variant="outlined" />}
                <Tooltip title={SL_TOOLTIP} arrow placement="right">
                  <IconButton size="small"><Info sx={{ fontSize: 18 }} /></IconButton>
                </Tooltip>
              </Box>
              <Slider
                value={stopLoss}
                onChange={(_, v) => setStopLoss(v as number)}
                min={0.5}
                max={15}
                step={0.5}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ color: 'error.main' }}
                marks={[
                  { value: 1, label: '1%' },
                  { value: 3, label: '3%' },
                  { value: 5, label: '5%' },
                  { value: 10, label: '10%' },
                  { value: 15, label: '15%' },
                ]}
              />
              {defaults && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(239,83,80,0.06)', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="error.main">Warum {defaults.sl}% für {STRATEGY_LABELS[strategyType]}?</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {defaults.slReason}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
                <Tooltip title="Engerer Stop = weniger Risiko, aber öfter ausgestoppt. Gut für volatile Märkte, wenn du Verluste minimieren willst." arrow>
                  <Chip label="Eng (1-2%): Wenig Risiko, oft ausgelöst" size="small" variant="outlined" sx={{ cursor: 'help' }} />
                </Tooltip>
                <Tooltip title="Mittlerer Stop = Balance zwischen Schutz und Spielraum. Für die meisten Strategien optimal." arrow>
                  <Chip label="Mittel (3-5%): Ausgewogen" size="small" variant="outlined" color="warning" sx={{ cursor: 'help' }} />
                </Tooltip>
                <Tooltip title="Weiter Stop = mehr Spielraum für den Trade, aber höheres Risiko pro Position. Nur für erfahrene Trader und langfristige Strategien." arrow>
                  <Chip label="Weit (6-15%): Viel Spielraum, mehr Risiko" size="small" variant="outlined" color="error" sx={{ cursor: 'help' }} />
                </Tooltip>
              </Box>
            </CardContent>
          </Card>

          {/* Take-Profit */}
          <Card sx={{ borderRadius: 2, mb: 2, border: 1, borderColor: 'success.main', borderStyle: 'solid' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="success.main">
                  Take-Profit: {takeProfit}%
                </Typography>
                {tpIsDefault && <Chip label="Empfohlen" size="small" color="success" variant="outlined" />}
                <Tooltip title={TP_TOOLTIP} arrow placement="right">
                  <IconButton size="small"><Info sx={{ fontSize: 18 }} /></IconButton>
                </Tooltip>
              </Box>
              <Slider
                value={takeProfit}
                onChange={(_, v) => setTakeProfit(v as number)}
                min={1}
                max={30}
                step={0.5}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ color: 'success.main' }}
                marks={[
                  { value: 2, label: '2%' },
                  { value: 5, label: '5%' },
                  { value: 10, label: '10%' },
                  { value: 20, label: '20%' },
                  { value: 30, label: '30%' },
                ]}
              />
              {defaults && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(76,175,80,0.06)', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="success.main">Warum {defaults.tp}% für {STRATEGY_LABELS[strategyType]}?</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {defaults.tpReason}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
                <Tooltip title="Niedriges Ziel wird häufiger erreicht. Ideal für Scalping und kurzfristige Strategien mit vielen Trades." arrow>
                  <Chip label="Niedrig (1-3%): Oft erreicht, kleine Gewinne" size="small" variant="outlined" sx={{ cursor: 'help' }} />
                </Tooltip>
                <Tooltip title="Mittleres Ziel bietet gute Balance. Passend für die meisten Strategien und Marktbedingungen." arrow>
                  <Chip label="Mittel (4-10%): Ausgewogen" size="small" variant="outlined" color="success" sx={{ cursor: 'help' }} />
                </Tooltip>
                <Tooltip title="Hohes Ziel wird seltener erreicht, bringt aber viel pro Trade. Nur für Trend- und Breakout-Strategien sinnvoll." arrow>
                  <Chip label="Hoch (10-30%): Selten, aber groß" size="small" variant="outlined" color="warning" sx={{ cursor: 'help' }} />
                </Tooltip>
              </Box>
            </CardContent>
          </Card>

          {/* Mindest-Trade-Betrag */}
          <Card sx={{ borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Mindest-Trade-Betrag
              </Typography>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Wofür ist das gut?
                    </Typography>
                    <Typography variant="caption" component="div">
                      Bitvavo nimmt 0,25% Gebühr pro Trade. Bei kleinen Beträgen frisst das die Rendite.
                      Mit dem Mindest-Trade-Betrag stellst du sicher, dass nur lohnende Orders ausgeführt werden.
                      Das absolute Minimum bei Bitvavo ist 5&nbsp;EUR.
                    </Typography>
                  </Box>
                }
                arrow
              >
                <IconButton size="small"><Info fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                label="Mindestbetrag (EUR)"
                value={minTradeAmount || ''}
                onChange={(e) => setMinTradeAmount(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, step: 5 }}
                helperText={minTradeAmount > 0 && minTradeAmount < 5 ? 'Mindestens 5 EUR (Bitvavo-Limit)' : '0 = kein Limit'}
                error={minTradeAmount > 0 && minTradeAmount < 5}
                sx={{ flex: '1 1 200px' }}
              />
              <FormControl sx={{ flex: '1 1 240px' }} disabled={minTradeAmount === 0}>
                <InputLabel>Verhalten</InputLabel>
                <Select
                  value={minTradeBehavior}
                  label="Verhalten"
                  onChange={(e) => setMinTradeBehavior(e.target.value as 'skip' | 'round_up')}
                >
                  <MenuItem value="skip">Trade überspringen</MenuItem>
                  <MenuItem value="round_up">Auf Mindestbetrag aufrunden</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {minTradeAmount > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {minTradeBehavior === 'skip'
                  ? `Wenn ein Signal weniger als ${minTradeAmount} EUR handeln will, wird der Trade übersprungen — der Bot wartet auf ein stärkeres Signal.`
                  : `Wenn ein Signal weniger als ${minTradeAmount} EUR handeln will, wird die Order auf ${minTradeAmount} EUR aufgestockt (sofern Cash + Budget ausreichen). Erhöht die Trade-Frequenz.`}
              </Alert>
            )}
          </Card>

          {/* Risk/Reward Anzeige */}
          <Card sx={{ borderRadius: 2, p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Risk/Reward Verhältnis</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color={takeProfit / stopLoss >= 1.5 ? 'success.main' : takeProfit / stopLoss >= 1 ? 'warning.main' : 'error.main'}>
                  1 : {(takeProfit / stopLoss).toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Für jeden €1 Risiko sind €{(takeProfit / stopLoss).toFixed(1)} Gewinn möglich
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Tooltip title="Ein R:R von mindestens 1:1.5 gilt als gut. Ab 1:2 ist es sehr profitabel — selbst wenn nur 40% der Trades gewinnen, bist du im Plus." arrow>
                  <Chip
                    label={takeProfit / stopLoss >= 2 ? 'Sehr gut' : takeProfit / stopLoss >= 1.5 ? 'Gut' : takeProfit / stopLoss >= 1 ? 'Akzeptabel' : 'Riskant'}
                    color={takeProfit / stopLoss >= 1.5 ? 'success' : takeProfit / stopLoss >= 1 ? 'warning' : 'error'}
                    sx={{ cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setActiveStep(1)}>Zurück</Button>
            <Button variant="contained" onClick={() => setActiveStep(3)}>Weiter zum Budget</Button>
          </Box>
        </Box>
        );
      })()}

      {/* ============ BUDGET STEP (shared) ============ */}
      {activeStep === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>Budget zuweisen</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Verfügbares Budget: €{availableBudget.toLocaleString('de-DE')}
          </Typography>

          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h4" textAlign="center" fontWeight={700} color="primary" sx={{ mb: 2 }}>
              €{budgetEur.toLocaleString('de-DE')}
            </Typography>
            <Slider
              value={budgetEur}
              onChange={(_, v) => setBudgetEur(v as number)}
              min={100}
              max={Math.max(availableBudget, 100)}
              step={100}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `€${v.toLocaleString('de-DE')}`}
            />

            {/* Summary */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Zusammenfassung</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Strategie</Typography>
                  <Typography variant="body2" fontWeight={600}>{STRATEGY_LABELS[strategyType] || strategyType}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Markt</Typography>
                  <Typography variant="body2" fontWeight={600}>{market}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Intervall</Typography>
                  <Typography variant="body2" fontWeight={600}>{INTERVAL_LABELS[intervalSeconds] || `${intervalSeconds}s`}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Stop-Loss</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">{stopLoss}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Take-Profit</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">{takeProfit}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Risk/Reward</Typography>
                  <Typography variant="body2" fontWeight={600}>1 : {(takeProfit / stopLoss).toFixed(1)}</Typography>
                </Box>
              </Box>
            </Box>
          </Card>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={() => setActiveStep(2)}>Zurück</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={loading || budgetEur > availableBudget}
            >
              {loading ? <CircularProgress size={24} /> : 'Strategie erstellen'}
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default StrategyWizard;
